import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Paper as MuiPaper,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import HomeIcon from "@mui/icons-material/Home";
import WithdrawStep1 from "./components/WithdrawStep1";
import WithdrawStep2 from "./components/WithdrawStep2";
import WithdrawStep3 from "./components/WithdrawStep3";
import WithdrawStep4 from "./components/WithdrawStep4";
import { checkPermissions } from "../../utils/checkPermissions";
import {
  GAME_USER_WITHDRAWAL_CANCEL,
  GAME_USER_WITHDRAWAL_CONFIRM,
  GAME_USER_WITHDRAWAL_FINANCIER_REVIEW,
  GAME_USER_WITHDRAWAL_PENDING,
  GAME_USER_WITHDRAWAL_REVIEW,
} from "../../constants";
import useAuth from "../../hooks/useAuth";
import useConfirm from "../../components/useConfirm";

const Divider = styled(MuiDivider)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

const getCurrentStatusProps = (status) => {
  switch (status) {
    case "pending":
      return {
        previousStatus: null,
        nextStatus: "reviewed",
        previousPermission: null,
        nextPermission: GAME_USER_WITHDRAWAL_REVIEW,
        step: 0,
        component: WithdrawStep1,
      };
    case "reviewed":
      return {
        previousStatus: "pending",
        nextStatus: "financier-reviewed",
        previousPermission: GAME_USER_WITHDRAWAL_PENDING,
        nextPermission: GAME_USER_WITHDRAWAL_FINANCIER_REVIEW,
        step: 1,
        component: WithdrawStep2,
      };
    case "financier_reviewed":
      return {
        previousStatus: "reviewed",
        nextStatus: "confirmed",
        previousPermission: GAME_USER_WITHDRAWAL_REVIEW,
        nextPermission: GAME_USER_WITHDRAWAL_CONFIRM,
        step: 2,
        component: WithdrawStep3,
      };
    case "confirmed":
      return {
        previousStatus: "financier-reviewed",
        nextStatus: null,
        previousPermission: GAME_USER_WITHDRAWAL_FINANCIER_REVIEW,
        nextPermission: null,
        step: 3,
        component: WithdrawStep4,
      };
    default:
      return {
        previousStatus: null,
        nextStatus: null,
        previousPermission: null,
        nextPermission: null,
        step: 3,
        component: WithdrawStep4,
      };
  }
};

function WithdrawView() {
  const { withdrawId } = useParams();
  const { permissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [withdrawOptions, setWithdrawOptions] = useState(null);
  const [withdraw, setWithdraw] = useState({});
  const [userOptions, setUserOptions] = useState(null);
  const [user, setUser] = useState({});

  const getCurrentStatusPropsCallback = useCallback(
    () => getCurrentStatusProps(withdraw.status),
    [withdraw.status]
  );
  const {
    previousStatus = null,
    nextStatus = null,
    previousPermission = null,
    nextPermission = null,
    step = 3,
    component = WithdrawStep4,
  } = getCurrentStatusPropsCallback();

  const [activeStep, setActiveStep] = useState(3);

  const getWithdraw = useCallback(async () => {
    try {
      setLoading(true);

      const { data: withdrawOptions } = await axios.options(
        `/api/v1/withdrawals/${withdrawId}`
      );
      setWithdrawOptions(withdrawOptions);

      const { data: withdraw } = await axios.get(
        `/api/v1/withdrawals/${withdrawId}`
      );
      setWithdraw(withdraw);

      const { data: userOptions } = await axios.options(
        `/api/v1/users/${withdraw.userId}`
      );
      setUserOptions(userOptions);

      const { data: user } = await axios.get(
        `/api/v1/users/${withdraw.userId}`
      );
      setUser(user);

      setActiveStep(step);

      setLoading(false);
    } catch (e) {
      setError(e.message);
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setWithdraw, step, withdrawId]);

  const updatePreviousWithdraw = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.post(
        `/api/v1/withdrawals/${withdrawId}/${previousStatus}`
      );
      setWithdraw(data);

      setActiveStep(step);
    } catch (e) {
      setError(e.message);
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setWithdraw, previousStatus, step, withdrawId]);

  const updateNextWithdraw = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.post(
        `/api/v1/withdrawals/${withdrawId}/${nextStatus}`
      );
      setWithdraw(data);

      setActiveStep(step);
    } catch (e) {
      setError(e.message);
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setWithdraw, nextStatus, step, withdrawId]);

  const [ConfirmDialog, confirmAction] = useConfirm(
    "Are you sure you want to update user?"
  );
  const cancelWithdraw = useCallback(async () => {
    const isConfirmed = await confirmAction();

    if (!isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `/api/v1/withdrawals/${withdrawId}/cancel`
      );
      setWithdraw(data);

      setActiveStep(step);
    } catch (e) {
      setError(e.message);
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setWithdraw, step, withdrawId]);

  useEffect(() => {
    getWithdraw();
  }, [getWithdraw]);

  if (error || !Number.isInteger(activeStep)) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={`Withdraw ${withdrawId}`} />

      <Typography variant="h3" gutterBottom display="inline">
        Withdraw {withdrawId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/withdraws">
          Withdraws
        </Link>
        <Typography>{withdrawId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper px={6} py={2} elevation={0}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm="auto">
                <Stepper
                  activeStep={activeStep}
                  sx={{ width: 350, maxWidth: "100%" }}
                >
                  {[0, 1, 2].map((step) => (
                    <Step key={step} completed={step < activeStep}>
                      <StepLabel />
                    </Step>
                  ))}
                </Stepper>
              </Grid>
              <Grid item>
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  disabled={
                    loading ||
                    withdraw.status === "canceled" ||
                    !checkPermissions(
                      [GAME_USER_WITHDRAWAL_CANCEL],
                      permissions
                    )
                  }
                  onClick={
                    withdraw.status === "canceled" ? () => {} : cancelWithdraw
                  }
                >
                  Cancel Withdraw
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {React.createElement(component, {
          loading,
          withdraw,
          withdrawOptions,
          user,
          userOptions,
          previousDisabled:
            !checkPermissions([previousPermission], permissions) ||
            !previousStatus,
          nextDisabled:
            !checkPermissions([nextPermission], permissions) || !nextStatus,
          onPrevious: updatePreviousWithdraw,
          onNext: updateNextWithdraw,
        })}
      </Grid>
      <ConfirmDialog />
    </React.Fragment>
  );
}

export default WithdrawView;
